import { ProgressEvent } from '../../../adapters';
import { WindowEvents } from '../../../analytics';
import {
    ContentDuration,
    IContent,
    MediaControllerState,
    PlanetMediaPlayer,
    PlanetUserInteractionArgs,
    PlaybackSessionState,
    TimeSpan,
} from '../../../media-player';
import { ScriptLoader } from '../../../utils';
import { VideoAnalyticsTrackerType } from './enums';
import { GoogleAnalyticsAction } from './enums/google-analytics-action';
import { GoogleAnalyticsCategory } from './enums/google-analytics-category';
import { GoogleAnalyticsOptions } from './models/google-analytics-options';
import { PlaybackSessionTracker } from './playback-session-tracker';

declare var gtag;

export class GoogleAnalyticsTracker extends PlaybackSessionTracker<VideoAnalyticsTrackerType.GoogleAnalytics, GoogleAnalyticsOptions> {

    private scriptLoader: ScriptLoader;

    private playbackSession: PlaybackSessionState;

    constructor() {
        super();
        this.scriptLoader = new ScriptLoader();
    }

    async setup(player: PlanetMediaPlayer, options: GoogleAnalyticsOptions): Promise<PlaybackSessionTracker<VideoAnalyticsTrackerType.GoogleAnalytics, GoogleAnalyticsOptions>> {
        await super.setup(player, options);
        this.options = options;
        const trackingId: string = options?.trackingId;
        const userId: string = options?.userId;
        if (trackingId) {
            const url: string = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
            await this.scriptLoader.requireAsync(url);

            let analyticsCode = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        `;
            if (userId) {
                analyticsCode += `
        gtag('config', '${trackingId}', {
           'user_id': '${userId}'
        });`;
            } else {
                analyticsCode += `gtag('config', '${trackingId}')`;
            }
            await this.scriptLoader.requireCode(analyticsCode);
        }

        window.addEventListener(WindowEvents.BeforeUnload, (e: BeforeUnloadEvent) => {
            this.onBeforeUnload();
        });

        return this;
    }

    dispose(): void {
    }

    onError(error: Error): void {
    }

    onSetupError(setupError: Error): void {
    }

    onUserInteraction(event: PlanetUserInteractionArgs): void {
    }

    trackContentLoad(content: IContent, index: number): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoLoaded,
            this.playbackSession?.id,
        );
    }

    trackPlaybackComplete(): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoComplete,
            this.playbackSession?.id,
        );
    }

    trackPlaybackPause(position: number, duration: ContentDuration, state: MediaControllerState, played: TimeSpan): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoPause,
            this.playbackSession?.id,
        );
    }

    trackPlaybackResume(position: number, duration: ContentDuration): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoResume,
            this.playbackSession?.id,
        );
    }

    trackPlaybackStart(position: number, duration: ContentDuration): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoStart,
            this.playbackSession?.id,
        );
    }

    trackSeek(position: number): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoSeek,
            this.playbackSession?.id,
            position,
        );
    }

    trackSessionCreate(session: PlaybackSessionState): void {
        this.playbackSession = session;
    }

    trackSessionEnd(session: PlaybackSessionState): void {
        this.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategory.Video,
            GoogleAnalyticsAction.VideoEnd,
            session?.id,
        );
    }

    trackStateChange(oldState: MediaControllerState, newState: MediaControllerState): void {
    }

    onBufferChange(data: ProgressEvent): void {
        let percentile: number = parseInt((data.percentile).toFixed(0));
        const trackedPercentiles = [5, 10, 25, 50, 75, 100];
        if (trackedPercentiles.some(v => percentile >= v)) {
            if (!trackedPercentiles.some(v => v === percentile)) {
                percentile = Math.max(...trackedPercentiles.filter(v => v < percentile));
            }
            if (!this.playbackSession.percentiles.some(p => p === percentile)) {
                this.playbackSession.percentiles.push(percentile);
                this.sendGoogleAnalyticsEvent(
                    GoogleAnalyticsCategory.Video,
                    GoogleAnalyticsAction.VideoProgress,
                    this.playbackSession?.id,
                    percentile,
                );
            }
        }
    }

    private onBeforeUnload(): void {
        this.sendGoogleAnalyticsEvent(GoogleAnalyticsCategory.PageView, GoogleAnalyticsAction.Bounce);
    }

    private sendGoogleAnalyticsEvent(eventCategory: GoogleAnalyticsCategory, eventAction: GoogleAnalyticsAction, contentGuid?: string, eventValue?: number): void {
        gtag('event', eventCategory, {'action': eventAction, 'content_guid': contentGuid, 'value': eventValue});
    }
}
